<template>
  <div class="modal" :class="{ 'is-active': is_open }">
    <div class="modal-background" @click="close"></div>
    <div class="modal-card is-fullscreen">
      <header class="modal-card-head">
        <p class="modal-card-title">
          {{ is_editing ? $t("edit ruleset") : $t("new ruleset") }}
        </p>
        <button class="delete" @click="close"></button>
      </header>
      <section class="modal-card-body">
        <form @submit.prevent="save">
          <!-- Basic fields -->
          <div class="columns">
            <div class="column is-7">
              <label class="label">{{ $t("name") }} *</label>
              <div class="control">
                <input
                  type="text"
                  class="input"
                  v-model="form.name"
                  :placeholder="$t('enter name')"
                  required
                />
              </div>
            </div>
            <div class="column">
              <label class="label">{{ $t("status") }} *</label>
              <div class="control">
                <div class="select is-fullwidth">
                  <select v-model="form.status" required>
                    <option value="">{{ $t("select status") }}</option>
                    <option value="active">{{ $t("active") }}</option>
                    <option value="inactive">{{ $t("inactive") }}</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div class="field">
            <label class="label">{{ $t("description") }}</label>
            <div class="control">
              <textarea
                class="textarea"
                v-model="form.description"
                :placeholder="$t('enter description')"
                rows="2"
              ></textarea>
            </div>
          </div>

          <!-- Rules manager with vertical tabs -->
          <div class="title is-6">{{ $t("rules") }}</div>
          <generic-dropdown
            :title="$t('select rule to add')"
            :placeholder="$t('select one')"
            :is_multiple="false"
            url="/credit-analysis/rule/catalog"
            name_field="name"
            value_field="rule_id"
            @selected="rule_selected"
            ref="rule_dropdown"
          />
          <div
            class="list mt-4"
            v-if="
              form.settings?.rule_list && form.settings?.rule_list.length > 0
            "
          >
            <div
              v-for="(rule, index) in form.settings?.rule_list"
              :key="index"
              class="list-item mb-2"
            >
              <div class="tags has-addons are-medium">
                <span class="tag is-info">{{ rule.name }}</span>
                <a class="tag is-delete" @click="remove_rule_list(index)"></a>
              </div>
              <!-- <span class="icon">
                <i class="fa fa-tag"></i>
              </span>
              <span>{{ rule.name }}</span> -->
            </div>
          </div>
          <div
            v-if="
              !form.settings?.rule_list || form.settings?.rule_list.length === 0
            "
            class="notification is-dark has-text-centered mt-4"
          >
            <p class="">{{ $t("no rules created yet") }}</p>
          </div>

          <div
            class="columns"
            v-if="
              form.settings &&
              form.settings.rules &&
              form.settings.rules.length > 0
            "
          >
            <!-- Vertical tabs -->
            <div class="column is-3">
              <div class="tabs is-vertical">
                <ul>
                  <li
                    v-for="(rule, rule_index) in form.settings.rules"
                    :key="rule_index"
                    :class="{ 'is-active': active_rule_tab === rule_index }"
                  >
                    <a @click="active_rule_tab = rule_index" class="tab-link">
                      <span class="icon is-small">
                        <i class="fa fa-cog"></i>
                      </span>
                      <span>
                        {{ rule.name || $t("rule") + " " + (rule_index + 1) }}
                      </span>
                      <button
                        class="delete is-small ml-2"
                        type="button"
                        @click.stop="remove_rule(rule_index)"
                        v-if="form.settings.rules.length > 1"
                      ></button>
                    </a>
                  </li>
                </ul>
              </div>

              <!-- Add new rule button -->
              <button
                class="button is-primary is-fullwidth mt-3"
                type="button"
                @click="add_rule"
              >
                <span class="icon">
                  <i class="fa fa-plus"></i>
                </span>
                <span>{{ $t("add rule") }}</span>
              </button>
            </div>

            <!-- Tab content -->
            <div class="column is-9">
              <div class="box rule-content pt-0">
                <div v-if="form.settings.rules[active_rule_tab]">
                  <!-- Rule Name Field -->
                  <div class="columns">
                    <div class="column">
                      <!-- <div class="field"> -->
                      <label class="label">{{ $t("rule name") }} *</label>
                      <div class="control">
                        <input
                          type="text"
                          class="input"
                          v-model="form.settings.rules[active_rule_tab].name"
                          :placeholder="$t('enter rule name')"
                          required
                        />
                      </div>
                      <!-- </div> -->
                    </div>
                    <div class="column">
                      <!-- <div class="field"> -->
                      <label class="label">{{ $t("type") }} *</label>
                      <div class="control">
                        <div class="select">
                          <select
                            v-model="form.settings.rules[active_rule_tab].type"
                          >
                            <option disabled value="">
                              {{ $t("select type") }}
                            </option>
                            <option value="direct_debt_history">
                              {{ $t("direct debt history") }}
                            </option>
                            <option value="legal_process">
                              {{ $t("legal process") }}
                            </option>
                          </select>
                        </div>
                      </div>
                      <!-- </div> -->
                    </div>
                  </div>

                  <!-- Direct debt history conditions -->
                  <div
                    v-show="
                      form.settings.rules[active_rule_tab].type ===
                      'direct_debt_history'
                    "
                  >
                    <div class="title is-6">
                      {{ $t("direct debt history conditions") }}
                      <button
                        class="button is-link is-small is-pulled-right"
                        type="button"
                        @click="add_condition(active_rule_tab)"
                      >
                        <span class="icon">
                          <i class="fa fa-plus"></i>
                        </span>
                        <span>{{ $t("add condition") }}</span>
                      </button>
                    </div>
                    <div
                      v-for="(condition, condition_index) in form.settings
                        .rules[active_rule_tab].direct_debt_history_conditions"
                      :key="condition_index"
                      class="field is-grouped mb-3"
                    >
                      <div class="control fixed-width-control">
                        <div class="select">
                          <select v-model="condition.field">
                            <option disabled value="">
                              {{ $t("select field") }}
                            </option>
                            <option value="date">{{ $t("date") }}</option>
                            <option value="total">{{ $t("total") }}</option>
                            <option value="max_capital_delay">
                              {{ $t("max capital delay") }}
                            </option>
                            <option value="max_interest_delay">
                              {{ $t("max interest delay") }}
                            </option>
                            <option value="total_capital_delay">
                              {{ $t("total capital delay") }}
                            </option>
                            <option value="total_interest_delay">
                              {{ $t("total interest delay") }}
                            </option>
                            <option value="classification">
                              {{ $t("classification") }}
                            </option>
                          </select>
                        </div>
                      </div>

                      <div class="control fixed-width-control">
                        <div class="select">
                          <select v-model="condition.operator">
                            <option disabled value="">
                              {{ $t("select operator") }}
                            </option>
                            <option value="equals">{{ $t("equals") }}</option>
                            <option value="not_equals">
                              {{ $t("not equals") }}
                            </option>
                            <option value="greater">
                              {{ $t("greater than") }}
                            </option>
                            <option value="greater_equals">
                              {{ $t("greater than or equal to") }}
                            </option>
                            <option value="less">{{ $t("less than") }}</option>
                            <option value="less_equals">
                              {{ $t("less than or equal to") }}
                            </option>
                            <option value="between">{{ $t("between") }}</option>
                            <option
                              value="contains"
                              v-if="!['date'].includes(condition.field)"
                            >
                              {{ $t("contains") }}
                            </option>
                          </select>
                        </div>
                      </div>

                      <template v-if="condition.operator === 'between'">
                        <div class="control fixed-width-control">
                          <input
                            class="input"
                            type="text"
                            v-model="condition.value_start"
                            :placeholder="
                              condition.field == 'date'
                                ? $t('start index')
                                : $t('start value')
                            "
                          />
                        </div>
                        <div class="control fixed-width-control">
                          <input
                            class="input"
                            type="text"
                            v-model="condition.value_end"
                            :placeholder="
                              condition.field == 'date'
                                ? $t('end index')
                                : $t('end value')
                            "
                          />
                        </div>
                      </template>

                      <div class="control fixed-width-control" v-else>
                        <input
                          class="input"
                          type="text"
                          v-model="condition.value"
                          :placeholder="
                            condition.field == 'date'
                              ? $t('index')
                              : $t('value')
                          "
                        />
                      </div>

                      <div class="control fixed-width-control">
                        <button
                          class="button is-danger"
                          type="button"
                          @click="
                            remove_condition(active_rule_tab, condition_index)
                          "
                        >
                          <span class="icon">
                            <i class="fa fa-minus"></i>
                          </span>
                        </button>
                      </div>
                    </div>

                    <!-- Segment conditions -->
                    <div class="title is-6 mt-5">
                      {{ $t("segments") }}
                    </div>
                    <div class="columns">
                      <div class="column">
                        <div class="field">
                          <input
                            class="is-checkradio"
                            type="checkbox"
                            id="chk_segment_plus"
                            v-model="
                              form.settings.rules[active_rule_tab]
                                .segment_conditions.plus
                            "
                          />
                          <label for="chk_segment_plus">{{ $t("plus") }}</label>
                        </div>
                        <div class="field">
                          <input
                            class="is-checkradio"
                            type="checkbox"
                            id="chk_segment_extra"
                            v-model="
                              form.settings.rules[active_rule_tab]
                                .segment_conditions.extra
                            "
                          />
                          <label for="chk_segment_extra">{{
                            $t("extra")
                          }}</label>
                        </div>
                      </div>
                      <div class="column">
                        <div class="field">
                          <input
                            class="is-checkradio"
                            type="checkbox"
                            id="chk_segment_premium"
                            v-model="
                              form.settings.rules[active_rule_tab]
                                .segment_conditions.premium
                            "
                          />
                          <label for="chk_segment_premium">{{
                            $t("premium")
                          }}</label>
                        </div>
                        <div class="field">
                          <input
                            class="is-checkradio"
                            type="checkbox"
                            id="chk_segment_ultra"
                            v-model="
                              form.settings.rules[active_rule_tab]
                                .segment_conditions.ultra
                            "
                          />
                          <label for="chk_segment_ultra">{{
                            $t("ultra")
                          }}</label>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- legal process conditions -->
                  <div
                    v-if="
                      form.settings.rules[active_rule_tab].type ===
                      'legal_process'
                    "
                  >
                    <div class="title is-6">
                      {{ $t("legal process conditions") }}
                    </div>

                    <div class="columns">
                      <div class="column">
                        <div class="field">
                          <input
                            class="is-checkradio"
                            type="checkbox"
                            id="chk_detalle_directo_vencido_cobro_judicial"
                            v-model="
                              form.settings.rules[active_rule_tab]
                                .legal_process_conditions
                                .expired_in_judicial_collection
                            "
                          />
                          <label
                            for="chk_detalle_directo_vencido_cobro_judicial"
                            >{{ $t("expired in judicial collection") }}</label
                          >
                        </div>
                        <div class="field">
                          <input
                            class="is-checkradio"
                            type="checkbox"
                            id="chk_detalle_directo_vencido_cobro_administrativo"
                            v-model="
                              form.settings.rules[active_rule_tab]
                                .legal_process_conditions
                                .expired_in_administrative_collection_process
                            "
                          />
                          <label
                            for="chk_detalle_directo_vencido_cobro_administrativo"
                            >{{
                              $t("expired in administrative collection process")
                            }}</label
                          >
                        </div>
                        <div class="field">
                          <input
                            class="is-checkradio"
                            type="checkbox"
                            id="chk_detalle_directo_vencido_proceso_prorroga"
                            v-model="
                              form.settings.rules[active_rule_tab]
                                .legal_process_conditions
                                .expired_in_extension_process
                            "
                          />
                          <label
                            for="chk_detalle_directo_vencido_proceso_prorroga"
                            >{{ $t("expired in extension process") }}</label
                          >
                        </div>
                        <div class="field">
                          <input
                            class="is-checkradio"
                            type="checkbox"
                            id="chk_detalle_declarado_incobrable"
                            v-model="
                              form.settings.rules[active_rule_tab]
                                .legal_process_conditions.declared_uncollectible
                            "
                          />
                          <label for="chk_detalle_declarado_incobrable">{{
                            $t("declared uncollectible")
                          }}</label>
                        </div>
                      </div>
                      <div class="column is-4">
                        <label>{{ $t("limit date to check") }}</label>
                        <div class="field has-addons mb-1">
                          <div class="control">
                            <input
                              class="input"
                              type="text"
                              v-model="
                                form.settings.rules[active_rule_tab]
                                  .legal_process_conditions.limit_date_to_check
                              "
                            />
                          </div>
                          <div class="control">
                            <div class="button is-static is-info">
                              {{ $t("years") }}
                            </div>
                          </div>
                        </div>
                        <div
                          class="help"
                          v-if="
                            form.settings.rules[active_rule_tab]
                              .legal_process_conditions.limit_date_to_check
                          "
                        >
                          {{
                            calculate_date(
                              form.settings.rules[active_rule_tab]
                                .legal_process_conditions.limit_date_to_check
                            )
                          }}
                        </div>
                        <!-- <input
                          class="input"
                          type="date"
                          v-model="
                            form.settings.rules[active_rule_tab]
                              .legal_process_conditions.limit_date_to_check
                          "
                        /> -->
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Initial add rule button when no rules exist -->
          <!-- <div v-else class="has-text-centered">
            <p class="mb-4">{{ $t("no rules created yet") }}</p>
            <button
              class="button is-primary is-large"
              type="button"
              @click="add_rule"
            >
              <span class="icon">
                <i class="fa fa-plus"></i>
              </span>
              <span>{{ $t("add first rule") }}</span>
            </button>
          </div> -->
        </form>
      </section>

      <footer class="modal-card-foot">
        <button
          class="button is-success is-rounded"
          @click="save"
          :disabled="is_loading"
        >
          <span class="icon" v-if="is_loading">
            <i class="fa fa-spinner fa-spin"></i>
          </span>
          <span class="icon" v-else>
            <i class="fa fa-save"></i>
          </span>
          <span>{{ $t("save") }}</span>
        </button>
        <button class="button is-rounded ml-3" @click="close">
          {{ $t("cancel") }}
        </button>
      </footer>
    </div>
  </div>
</template>

<script>
import {
  get,
  post,
  put,
  success_message,
  error_message,
  warning_message,
  format_date,
} from "@/utils";
import GenericDropdown from "@/components/generic_dropdown/Index.vue";

export default {
  name: "CreditAnalysisRulesetModal",
  components: {
    GenericDropdown,
  },
  data() {
    return {
      is_open: false,
      is_editing: false,
      is_loading: false,
      uuid: null,
      active_rule_tab: 0,
      form: {
        name: "",
        description: "",
        status: "active",
        settings: { rules: [], rule_list: [] },
      },
    };
  },
  methods: {
    open(uuid = null) {
      this.uuid = uuid;
      this.is_editing = !!uuid;
      this.is_open = true;

      if (this.is_editing) {
        this.loadRuleset();
      } else {
        this.resetForm();
      }
    },
    close() {
      this.is_open = false;
      this.resetForm();
    },
    resetForm() {
      this.form = {
        name: "",
        description: "",
        status: "active",
        settings: { rules: [], rule_list: [] },
      };
      this.$refs.rule_dropdown?.clear();
      this.active_rule_tab = 0;
    },
    loadRuleset() {
      this.is_loading = true;
      get("/credit-analysis/ruleset", { uuid: this.uuid })
        .then((response) => {
          const data = response;
          this.form = {
            name: data.name || "",
            description: data.settings?.description || "",
            status: data.status || "",
            settings: data.settings || { rules: [], rule_list: [] },
          };
        })
        .catch((error) => {
          error_message(error);
        })
        .finally(() => {
          this.is_loading = false;
        });
    },
    save() {
      if (!this.form.name.trim()) {
        error_message(this.$t("name is required"));
        return;
      }

      // Validate that all rules have names
      for (let i = 0; i < this.form.settings.rules.length; i++) {
        const rule = this.form.settings.rules[i];
        if (!rule.name || !rule.name.trim()) {
          error_message(
            this.$t("rule name is required for rule") + " " + (i + 1)
          );
          return;
        }
      }

      this.is_loading = true;
      const settings = {
        ...this.form.settings,
        description: this.form.description.trim() || "",
      };
      const data = {
        name: this.form.name.trim(),
        status: this.form.status,
        settings: Object.keys(settings).length > 0 ? settings : {},
      };
      const request = this.is_editing
        ? post("/credit-analysis/ruleset", { ...data, uuid: this.uuid })
        : put("/credit-analysis/ruleset", data);

      request
        .then(() => {
          success_message(
            this.$t(
              this.is_editing
                ? "ruleset updated successfully"
                : "ruleset created successfully"
            )
          );
          this.$emit("saved");
          this.close();
        })
        .catch((error) => {
          error_message(error);
        })
        .finally(() => {
          this.is_loading = false;
        });
    },

    // Rules management
    add_rule() {
      if (!this.form.settings.rules) this.form.settings.rules = [];

      this.form.settings.rules.push({
        name: "",
        direct_debt_history_conditions: [
          // {
          //   field: "",
          //   operator: "",
          //   value: "",
          //   value_start: "",
          //   value_end: "",
          // },
        ],
        type: "",
        legal_process_conditions: {
          expired_in_judicial_collection: false,
          expired_in_extension_process: false,
          expired_in_administrative_collection_process: false,
          limit_date_to_check: "",
        },
        segment_conditions: {
          plus: false,
          extra: false,
          premium: false,
          ultra: false,
        },
      });

      // Activate the new rule tab
      this.active_rule_tab = this.form.settings.rules.length - 1;
    },
    remove_rule(index) {
      this.form.settings.rules.splice(index, 1);

      // Adjust active tab if necessary
      if (this.active_rule_tab >= this.form.settings.rules.length) {
        this.active_rule_tab = Math.max(0, this.form.settings.rules.length - 1);
      }
    },
    add_condition(rule_index) {
      this.form.settings.rules[rule_index].direct_debt_history_conditions.push({
        field: "",
        operator: "",
        value: "",
        value_start: "",
        value_end: "",
      });
    },
    remove_condition(rule_index, condition_index) {
      this.form.settings.rules[
        rule_index
      ].direct_debt_history_conditions.splice(condition_index, 1);
    },
    calculate_date(years) {
      if (!years || isNaN(years)) return "";
      const date = new Date();
      date.setFullYear(date.getFullYear() - years);
      // Return date in format DD/MM/YYYY
      return format_date(date);
    },
    rule_selected(rule) {
      if (rule && rule.rule_id) {
        if (!this.form.settings.rule_list) this.form.settings.rule_list = [];

        // Check if the rule is already added
        if (
          this.form.settings.rule_list.find((r) => r.rule_id == rule.rule_id)
        ) {
          warning_message(this.$t("rule already added"));
          return;
        }

        // Add rule to the list
        this.form.settings.rule_list.push({
          name: rule.name,
          rule_id: rule.rule_id,
        });
        setTimeout(() => {
          this.$refs.rule_dropdown?.clear();
        }, 200);
      }
    },
    remove_rule_list(index) {
      this.form.settings.rule_list.splice(index, 1);
    },
  },
};
</script>

<style scoped>
.modal.is-fullscreen .modal-card {
  width: 90vw;
  height: 90vh;
  max-width: 90vw;
  max-height: 90vh;
  margin: 0;
  border-radius: 15px;
}

.modal.is-fullscreen .modal-card-body {
  max-height: calc(100vh - 120px);
  overflow-y: auto;
  padding: 2rem;
}

.modal.is-fullscreen .modal-card-head {
  border-radius: 0;
}

.modal.is-fullscreen .modal-card-foot {
  border-radius: 0;
}

.help {
  font-size: 0.875rem;
  color: #888;
}

.field {
  margin-bottom: 1.5rem;
}

/* Vertical tabs styling */
.tabs.is-vertical {
  display: block;
}

.tabs.is-vertical ul {
  flex-direction: column;
  border-bottom: none;
  /* border-right: 1px solid #dbdbdb; */
}

.tabs.is-vertical li {
  display: block;
  width: 100%;
}

.tabs.is-vertical li:not(:last-child) {
  margin-right: 0;
  margin-bottom: 0;
}

.tabs.is-vertical li a {
  /* border-bottom: 1px solid #dbdbdb; */
  border-radius: 4px 0 0 4px;
  justify-content: flex-start;
  padding: 0.75em 1em;
  display: flex;
  align-items: center;
}

.tabs.is-vertical li.is-active a {
  background-color: #3273dc;
  border-color: #3273dc;
  color: #fff;
}

.tabs.is-vertical li a:hover {
  background-color: #646060;
  border-color: #b5b5b5;
}

.tabs.is-vertical li.is-active a:hover {
  background-color: #2366d1;
  border-color: #2366d1;
}

.tab-link {
  position: relative;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.rule-content {
  min-height: 400px;
}

.delete.is-small {
  width: 16px;
  height: 16px;
  min-width: 16px;
  min-height: 16px;
}

.fixed-width-control {
  max-width: 150px;
}

.help {
  font-size: 0.875rem;
  color: #888;
}
</style>
